import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ROLE_GESTOR_CONSULTA } from 'src/app/constantes/permissao.constants';
import { PermissaoService } from 'src/app/shared/permissao.service';
import { ModalidadeGarantiaService } from '../../modalidade_garantia/modalidade-garantia.service';
import { ModalidadeGarantia } from 'src/app/model/modalidade_garantia.model';
import { CalculoEcg, CalculoEcgRetorno } from 'src/app/model/calculo-ecg.model';
import { OperacaoGarantidaService } from '../operacao/operacao-garantida.service';
import { OperacaoGarantida } from 'src/app/model/operacao-garantida.model';
import { TrancheService } from 'src/app/shared/services/tranche.service';
import { Tranche } from 'src/app/model/tranche.model';
import { LiberacaoService } from '../operacao/liberacao/liberacao.service';
import { Liberacao } from 'src/app/model/liberacao.model';
import { BnLoadingService } from '@bndes/bndes-ux4/bndes-angular-utils';
import { OperacaoCreditoLivre } from 'src/app/model/operacao-credito-livre.model';

@Component({
  selector: 'app-calculo-ecg',
  templateUrl: './calculo-ecg.component.html',
  styleUrls: ['./calculo-ecg.component.scss'],
  standalone: false,
})
export class CalculoEcgComponent implements OnInit {

  public permissaoEnvio: boolean = false;
  public formCalculoECG: FormGroup;
  public formCalculoECGRetorno: FormGroup;
  public dataCorrente: Date = new Date();
  public percentuaisPorModalidade: number[];
  public dataFuturaUltimaAmortizacao: Date;
  public numerosTranches: number[];
  public mostrarResultado: boolean = false;

  constructor(
    private permissaoService: PermissaoService,
    private formBuilder: FormBuilder,
    private modalidadeGarantiaService: ModalidadeGarantiaService,
    private loadingService: BnLoadingService,
    private operacaoGarantidaService: OperacaoGarantidaService,
    private liberacaoService: LiberacaoService,
    private trancheService: TrancheService,
  ) { }

  ngOnInit(): void {
    this.permissaoService.getPermissoesUsuarioAutenticado().subscribe((resp) => {
      this.permissaoEnvio = resp && resp[ROLE_GESTOR_CONSULTA];
    });

    this.modalidadeGarantiaService.buscarModalidade().subscribe((modalidade: ModalidadeGarantia) => {
      this.percentuaisPorModalidade = modalidade.percentuaisGarantiaPermitidos;
    });

    this.trancheService.getTranches().subscribe((tranches: Tranche[]) => {
      this.numerosTranches = tranches.map(t => t.numeroTranche);
    });

    this.construirFormulario();
    this.construirFormRetorno();
    this.verificarValidadeDatas();
    this.verificarApenasUmCampo('idOperacaoCredito', 'idLiberacao');
  }

  construirFormulario() {
    this.formCalculoECG = this.formBuilder.group({
      idOperacaoCredito: null,
      idLiberacao: null,
      valor: [null, [Validators.required, Validators.min(0)]],
      numeroTranche: [null, [Validators.required]],
      percentualGarantidoDecimal: [
        this.percentuaisPorModalidade ? this.percentuaisPorModalidade[0] : 80,
        [Validators.min(0), Validators.max(90)],
      ],
      inicio: [null, Validators.required],
      dataUltimaAmortizacao: [{value: null, disabled: true}, Validators.required],
      fatorEncargo: [null, [Validators.required, Validators.min(0.0000)]],
      encargoEhGarantido: [false, Validators.required],
      consideraDescontoPcd: [false, Validators.required],
    });
  } 

  construirFormRetorno() {
    this.formCalculoECGRetorno = this.formBuilder.group({
      valorEcg: null,
      fatorRedutorECG: null,
      quantidadeDias: null,
      quantidadeMeses: null,
    });
  }

  calcular() {
    if (this.formCalculoECG.valid) {
      const valuesCalcEcg: CalculoEcg = {
        valor: this.formCalculoECG.get('valor').value,
        numeroTranche: this.formCalculoECG.get('numeroTranche').value,
        percentualGarantidoDecimal: this.formCalculoECG.get('percentualGarantidoDecimal').value/100.00,
        inicio: new Date(this.formCalculoECG.get('inicio').value),
        dataUltimaAmortizacao: this.formCalculoECG.get('dataUltimaAmortizacao').value,
        fatorEncargo: this.formCalculoECG.get('fatorEncargo').value,
        encargoEhGarantido: this.formCalculoECG.get('encargoEhGarantido').value,
        consideraDescontoPcd: this.formCalculoECG.get('consideraDescontoPcd').value,
      }

      this.formCalculoECG.updateValueAndValidity();

      this.operacaoGarantidaService.calcularEcg(valuesCalcEcg).subscribe((calculoEcg: CalculoEcgRetorno) => {
        this.formCalculoECGRetorno.patchValue({
          valorEcg: Number(calculoEcg.valorEcg) || null,
          fatorRedutorECG: Number(calculoEcg.fatorRedutorECG) * 100 || 0.00,
          quantidadeDias: calculoEcg.quantidadeDias,
          quantidadeMeses: calculoEcg.quantidadeMeses,
        });

        this.mostrarResultado = true;
      });
    }
  }

  carregar() {
    if (this.formCalculoECG.get('idOperacaoCredito').value) {
      this.buscarOperacao();
    } else {
      this.buscarLiberacao();
    }
  }

  buscarOperacao() { 
    this.loadingService.bloqueiaTela();

    const idOperacao = Number(this.formCalculoECG.get('idOperacaoCredito').value);
    if (idOperacao) {
      this.operacaoGarantidaService.findById(idOperacao).subscribe({
        next:(operacao: OperacaoGarantida) => {
          const dataInicio = this.ajustaCampoDate(operacao.dataContratacao);
          if(operacao.codigoTipoOperacao === 'R' && dataInicio.getDate() === 15) {
            dataInicio.setMonth(dataInicio.getMonth() + 1);
          }

          this.formCalculoECG.patchValue({
            valor: operacao.valorOperacao || null,
            numeroTranche: this.numerosTranches.includes(operacao.detalhe?.numeroTranche) ? operacao.detalhe?.numeroTranche : null,
            percentualGarantidoDecimal: Number(operacao.percentualGarantido),
            inicio: dataInicio,
            dataUltimaAmortizacao: operacao.codigoTipoOperacao === 'L' 
              ? this.ajustaCampoDate((operacao as OperacaoCreditoLivre).amortizacoes.at(-1)?.data) : null,
            fatorEncargo: Number(operacao.detalhe?.fatorK) || null,
            encargoEhGarantido: operacao.detalhe?.ecgDeveSerSomadoAoValorOperacao === 'S' ? true : false,
            consideraDescontoPcd: operacao.indicadorPessoaComDeficiencia === 'S' ? true : false,
          });
          
          this.formCalculoECG.updateValueAndValidity();
          this.loadingService.liberaTela();
        }, error: error => {
          this.loadingService.liberaTela();
          console.error(error);
        }
      });
    }
  }

  buscarLiberacao() { 
    this.loadingService.bloqueiaTela();

    const idLiberacao = Number(this.formCalculoECG.get('idLiberacao').value);
    if (idLiberacao) {
      this.liberacaoService.getLiberacao(idLiberacao).subscribe({
        next:(liberacao: Liberacao) => {
          const operacao:OperacaoGarantida = liberacao.operacao; 

          const dataInicio = this.ajustaCampoDate(liberacao.data);
          if(operacao.codigoTipoOperacao === 'R' && dataInicio.getDate() === 15) {
            dataInicio.setMonth(dataInicio.getMonth() + 1);
          }

          this.formCalculoECG.patchValue({
            valor: liberacao.valor || null,
            numeroTranche: this.numerosTranches.includes(operacao.detalhe?.numeroTranche) ? operacao.detalhe?.numeroTranche : null,
            percentualGarantidoDecimal: Number(operacao.percentualGarantido),
            inicio: dataInicio,
            dataUltimaAmortizacao: operacao.codigoTipoOperacao === 'L' 
              ? this.ajustaCampoDate((operacao as OperacaoCreditoLivre).amortizacoes.at(-1)?.data) : null,
            fatorEncargo: Number(operacao.detalhe?.fatorK) || null,
            encargoEhGarantido: operacao.detalhe?.ecgDeveSerSomadoAoValorOperacao === 'S' ? true : false,
            consideraDescontoPcd: operacao.indicadorPessoaComDeficiencia === 'S' ? true : false,
          });
          
          this.formCalculoECG.updateValueAndValidity();
          this.loadingService.liberaTela();
        }, 
        error: error => {
          this.loadingService.liberaTela();
          console.error(error);
        }
      })
      
    }
  }

  verificarValidadeDatas() {
    const dataInicioCtrl = this.formCalculoECG.get("inicio");
    const ultimaAmortCtrl = this.formCalculoECG.get("dataUltimaAmortizacao");

    dataInicioCtrl.valueChanges.subscribe((novaData) => {
      if (dataInicioCtrl.valid && novaData) {
        if (typeof novaData === "string") novaData = new Date(novaData);

        ultimaAmortCtrl.enable();
        this.dataFuturaUltimaAmortizacao = this.adicionaDias(novaData, 1); 
      } else {
        ultimaAmortCtrl.disable();
      }
      ultimaAmortCtrl.reset();
    });
  }

  adicionaDias(dataAntiga: Date, quantidadeDias: number = 1) {
    const novaData = new Date(dataAntiga);
    novaData.setDate(dataAntiga.getDate() + quantidadeDias);
    return novaData;
  }

  limpar() {
    this.formCalculoECG.reset({
      percentualGarantidoDecimal: this.percentuaisPorModalidade ? this.percentuaisPorModalidade[0] : 80,
      encargoEhGarantido: false,
      consideraDescontoPcd: false,
    });
    this.formCalculoECG.markAsPristine();

    this.mostrarResultado = false;
  }

  get podeCarregar() {
    const idOperacaoCredito = Number(this.formCalculoECG.get('idOperacaoCredito')?.value);
    const idLiberacao = Number(this.formCalculoECG.get('idLiberacao')?.value);
    return idOperacaoCredito || idLiberacao;
  }

  verificarApenasUmCampo(control1: string, control2: string) {
    const formControl1 = this.formCalculoECG.get(control1);
    const formControl2 = this.formCalculoECG.get(control2);

    if (!formControl1 || !formControl2) return;

    formControl1.valueChanges.subscribe((novoValor) => {
      if (novoValor) {
        formControl2.reset();
      }
    });
    formControl2.valueChanges.subscribe((novoValor) => {
      if (novoValor) {
        formControl1.reset();
      }
    });
  }

  ajustaCampoDate(data:any) {
    if (typeof data === 'string') {
      if (data.length == 10) {
        data += ' 00:00:00';
      }
      return new Date(data);
    }

    return data;
  }

}
